import { selectUser } from '~/selectors';
import { store } from '~/store';

export enum ApiResponseEnum {
  Success,
  Failure,
  NetworkFailure,
}

interface INetworkSuccess<T> {
  type: ApiResponseEnum.Success;
  data: T;
  msg?: string;
}

interface INetworkFailure {
  type: ApiResponseEnum.Failure;
  error: string;
  source: 'response' | 'network';
}

export type ApiResponseHandlerType<T> = INetworkSuccess<T> | INetworkFailure;

export type APIResponseType<T> =
  | {
      status: true;
      data: T;
      msg: string;
    }
  | {
      status: false;
      msg: string;
    };

function getDeviceInfo() {
  const ua = navigator.userAgent;

  let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
  if (/Mobi|Android/i.test(ua)) deviceType = 'mobile';
  if (/Tablet|iPad/i.test(ua)) deviceType = 'tablet';

  let os = 'Unknown';
  if (/Windows NT/i.test(ua)) os = 'Windows';
  else if (/Mac OS X/i.test(ua)) os = 'MacOS';
  else if (/Android/i.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/i.test(ua)) os = 'iOS';
  else if (/Linux/i.test(ua)) os = 'Linux';

  let browser = 'Unknown';
  if (/Chrome\/\d+/i.test(ua)) browser = 'Chrome';
  else if (/Safari\/\d+/i.test(ua) && !/Chrome\/\d+/i.test(ua)) browser = 'Safari';
  else if (/Firefox\/\d+/i.test(ua)) browser = 'Firefox';
  else if (/Edg\/\d+/i.test(ua)) browser = 'Edge';
  else if (/MSIE|Trident/i.test(ua)) browser = 'IE';

  return {
    deviceType,
    os,
    browser,
  };
}

export async function apiResponseHandler<T>(
  apiPromise: Promise<APIResponseType<T>>,
): Promise<ApiResponseHandlerType<T>> {
  try {
    const response = await apiPromise;
    if (response.status) {
      return {
        type: ApiResponseEnum.Success,
        data: response.data,
        msg: response.msg,
      };
    }
    return {
      type: ApiResponseEnum.Failure,
      error: response.msg,
      source: 'response',
    };
  } catch (error: any) {
    if (process.env.APP_ENV === 'development') {
      console.log(error);
    }

    return {
      type: ApiResponseEnum.Failure,
      error: error.toString(),
      source: 'network',
    };
  }
}

export const getMetadataHeaders = (): Record<string, string> => {
  const deviceInfo = getDeviceInfo();
  return {
    'Content-Type': 'application/json',
    'abs-referer': window.location.href,
    'page-referer': document.referrer,
    'X-Device-Type': deviceInfo.deviceType,
    'X-Device-OS': deviceInfo.os,
    'X-Device-Browser': deviceInfo.browser,
  };
};

export async function callApi<T>(
  method: 'put' | 'post' | 'get' | 'delete',
  endpoint: string,
  data?: T,
  responseType: 'json' | 'blob' = 'json',
) {
  const headers = { ...getMetadataHeaders() };

  const state = store.getState();
  const { token } = selectUser(state);
  if (token) {
    headers.Authorization = `Token ${token}`;
  }

  const options: Record<string, unknown> = {
    method,
    headers,
  };
  if (method !== 'get') {
    options.body = JSON.stringify(data);
  }

  const response = await fetch(process.env.SERVER_URL + endpoint, options);

  if (responseType === 'blob') {
    return response.blob();
  }

  return response.json();
}
